import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ClipboardList,
  Check,
  Ban,
  Building2,
  Store,
  PackageCheck,
  FileText,
  ArrowRight,
  Paperclip,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DetailField } from '@/components/common/DetailField';
import { DIALOG_TAB_TRIGGER, DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { procurementService } from '@/services/procurement';
import { vendorsService } from '@/services/vendors';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ProcurementSourcing, PurchaseRequest } from '@/types/procurement';
import { PR_STATUS_LABEL, PR_STATUS_TONE } from './PurchaseRequestsPage';
import { ReceiptFormSheet } from './ReceiptFormSheet';

interface Props {
  prId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PurchaseRequestDetailSheet({ prId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [action, setAction] = useState<'none' | 'approve' | 'reject'>('none');
  const [reviewerNote, setReviewerNote] = useState('');
  const [approveSourcing, setApproveSourcing] = useState<ProcurementSourcing>('VENDOR');
  const [approveVendorId, setApproveVendorId] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);

  const { data: pr, isLoading } = useQuery({
    queryKey: ['purchase-request', prId],
    queryFn: () => procurementService.getRequest(prId!),
    enabled: open && !!prId,
  });

  const receiptsQuery = useQuery({
    queryKey: ['purchase-request', prId, 'receipts'],
    queryFn: () => procurementService.listReceipts({ purchaseRequestId: prId!, pageSize: 50 }),
    enabled: open && !!prId,
  });

  const vendorsQuery = useQuery({
    queryKey: ['vendors', { forSelect: true }],
    queryFn: () => vendorsService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open && action === 'approve',
    staleTime: 60_000,
  });
  const vendors = vendorsQuery.data?.items ?? [];

  useEffect(() => {
    if (!open) return;
    setAction('none');
    setReviewerNote('');
  }, [open, prId]);

  useEffect(() => {
    if (pr) {
      setApproveSourcing(pr.sourcingType);
      setApproveVendorId(pr.proposedVendorId ?? '');
    }
  }, [pr]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['purchase-requests'] });
    qc.invalidateQueries({ queryKey: ['purchase-request', prId] });
  };

  const submitMut = useMutation({
    mutationFn: () => procurementService.submitRequest(prId!),
    onSuccess: () => {
      toast.success('Submitted for approval');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });

  const approveMut = useMutation({
    mutationFn: () =>
      procurementService.approveRequest(prId!, {
        sourcingType: approveSourcing,
        proposedVendorId:
          approveSourcing === 'VENDOR' && approveVendorId ? approveVendorId : undefined,
        reviewerNote: reviewerNote.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Request approved');
      setAction('none');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not approve'),
  });

  const rejectMut = useMutation({
    mutationFn: () => procurementService.rejectRequest(prId!, reviewerNote.trim()),
    onSuccess: () => {
      toast.success('Request rejected');
      setAction('none');
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not reject'),
  });

  const convertMut = useMutation({
    mutationFn: () => procurementService.convertToPo(prId!, {}),
    onSuccess: (updated) => {
      toast.success(
        updated.purchaseOrder ? `PO ${updated.purchaseOrder.poNumber} created` : 'Converted to PO',
      );
      invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not convert'),
  });

  const allDocs = useMemo(() => {
    const fromPr = pr?.attachments ?? [];
    const fromReceipts = (receiptsQuery.data?.rows ?? []).flatMap((r) => r.attachments ?? []);
    return [...fromPr, ...fromReceipts];
  }, [pr, receiptsQuery.data]);

  const busy =
    submitMut.isPending || approveMut.isPending || rejectMut.isPending || convertMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0">
        {isLoading || !pr ? (
          <div className="flex h-72 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-0 border-b border-border px-6 py-4">
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
                  <ClipboardList className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <DialogEyebrow>Purchase request</DialogEyebrow>
                  <DialogTitle className="flex items-center gap-2 font-mono text-[17px] font-semibold tracking-tight">
                    {pr.prNumber}
                    <Badge variant={PR_STATUS_TONE[pr.status]} className="text-[10px]">
                      {PR_STATUS_LABEL[pr.status]}
                    </Badge>
                  </DialogTitle>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    {pr.sourcingType === 'VENDOR' ? 'Vendor sourcing' : 'Open-market sourcing'} ·{' '}
                    {pr.items.length} item{pr.items.length === 1 ? '' : 's'} · created{' '}
                    {fmt.relative(pr.createdAt)}
                  </p>
                </div>
              </div>
            </DialogHeader>

            {/* ── Action bar ─────────────────────────────────────────── */}
            <ActionBar
              pr={pr}
              busy={busy}
              onSubmit={() => submitMut.mutate()}
              onStartApprove={() => setAction((a) => (a === 'approve' ? 'none' : 'approve'))}
              onStartReject={() => setAction((a) => (a === 'reject' ? 'none' : 'reject'))}
              onConvert={() => convertMut.mutate()}
              onReceive={() => setReceiptOpen(true)}
            />

            {action === 'approve' && (
              <div className="space-y-3 border-b border-border bg-muted/20 px-6 py-4">
                <SectionHeading title="Approve request" />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setApproveSourcing('VENDOR')}
                    className={cn(
                      'flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-left text-[12px]',
                      approveSourcing === 'VENDOR'
                        ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                        : 'border-border/70',
                    )}
                  >
                    <Building2 className="h-4 w-4 text-primary" /> Vendor
                  </button>
                  <button
                    type="button"
                    onClick={() => setApproveSourcing('OPEN_MARKET')}
                    className={cn(
                      'flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-left text-[12px]',
                      approveSourcing === 'OPEN_MARKET'
                        ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                        : 'border-border/70',
                    )}
                  >
                    <Store className="h-4 w-4 text-primary" /> Open market
                  </button>
                </div>
                {approveSourcing === 'VENDOR' && (
                  <Select value={approveVendorId} onValueChange={setApproveVendorId}>
                    <SelectTrigger className="h-9 text-[12.5px]">
                      <SelectValue placeholder="Select vendor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                            {v.code}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Textarea
                  value={reviewerNote}
                  onChange={(e) => setReviewerNote(e.target.value)}
                  placeholder="Note for the requester (optional)"
                  rows={2}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAction('none')}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={() => approveMut.mutate()} disabled={busy}>
                    <Check className="h-3.5 w-3.5" /> Confirm approval
                  </Button>
                </div>
              </div>
            )}

            {action === 'reject' && (
              <div className="space-y-3 border-b border-border bg-destructive/5 px-6 py-4">
                <SectionHeading title="Reject request" />
                <Textarea
                  value={reviewerNote}
                  onChange={(e) => setReviewerNote(e.target.value)}
                  placeholder="Reason for rejection (required)"
                  rows={2}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAction('none')}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => rejectMut.mutate()}
                    disabled={busy || !reviewerNote.trim()}
                  >
                    <Ban className="h-3.5 w-3.5" /> Confirm rejection
                  </Button>
                </div>
              </div>
            )}

            <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
              <TabsList className="h-auto justify-start gap-1 rounded-none border-b border-border bg-transparent px-6 pt-1">
                <TabsTrigger value="overview" className={DIALOG_TAB_TRIGGER}>
                  Overview
                </TabsTrigger>
                <TabsTrigger value="documents" className={DIALOG_TAB_TRIGGER}>
                  Documents{allDocs.length ? ` (${allDocs.length})` : ''}
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="max-h-[52vh] flex-1">
                <TabsContent value="overview" className="m-0 space-y-5 px-6 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <DetailField label="Sourcing">
                      {pr.sourcingType === 'VENDOR' ? 'Dedicated vendor' : 'Open market'}
                    </DetailField>
                    <DetailField label="Warehouse">
                      {pr.warehouse.name ?? pr.warehouse.code}
                    </DetailField>
                    {pr.proposedVendor && (
                      <DetailField label="Vendor">{pr.proposedVendor.name}</DetailField>
                    )}
                    {pr.neededBy && (
                      <DetailField label="Needed by">{fmt.date(pr.neededBy)}</DetailField>
                    )}
                    <DetailField label="Estimated total">
                      {pr.estimatedTotal != null ? fmt.inr(pr.estimatedTotal) : '—'}
                    </DetailField>
                    {pr.approvedAt && (
                      <DetailField label="Approved">{fmt.dateTime(pr.approvedAt)}</DetailField>
                    )}
                  </div>

                  {pr.justification && (
                    <DetailField label="Justification">{pr.justification}</DetailField>
                  )}
                  {pr.reviewerNote && (
                    <DetailField label="Reviewer note">{pr.reviewerNote}</DetailField>
                  )}

                  {pr.purchaseOrder && (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-[12.5px]">
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      Sourced via PO{' '}
                      <span className="font-mono font-medium">{pr.purchaseOrder.poNumber}</span>
                      <Badge variant="muted" className="ml-auto text-[10px]">
                        {pr.purchaseOrder.status}
                      </Badge>
                    </div>
                  )}

                  <div>
                    <SectionHeading title="Requested items" />
                    <div className="mt-2 space-y-1">
                      {pr.items.map((l) => {
                        const got = Number(l.receivedQuantity);
                        const qty = Number(l.quantity);
                        return (
                          <div
                            key={l.id}
                            className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-mono text-[11px] text-muted-foreground">{l.sku}</div>
                              <div className="truncate text-[12.5px] text-foreground">{l.name}</div>
                            </div>
                            <div className="text-right text-[12px] tabular-nums">
                              <span className={cn(got >= qty ? 'text-success' : 'text-foreground')}>
                                {fmt.number(got)}
                              </span>
                              <span className="text-muted-foreground"> / {fmt.number(qty)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {(receiptsQuery.data?.rows.length ?? 0) > 0 && (
                    <div>
                      <SectionHeading title="Goods receipts" />
                      <div className="mt-2 space-y-1">
                        {receiptsQuery.data!.rows.map((r) => (
                          <div
                            key={r.id}
                            className="flex items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 text-[12.5px]"
                          >
                            <PackageCheck className="h-3.5 w-3.5 text-success" />
                            <span className="font-mono">{r.grnNumber}</span>
                            <span className="ml-auto text-[11px] text-muted-foreground">
                              {fmt.dateTime(r.receivedAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="documents" className="m-0 space-y-2 px-6 py-4">
                  {allDocs.length === 0 ? (
                    <div className="py-8 text-center text-[12.5px] text-muted-foreground">
                      No documents attached yet. Invoice, challan and e-way bill are
                      attached when you record a goods receipt.
                    </div>
                  ) : (
                    allDocs.map((d) => (
                      <a
                        key={d.id}
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-2 text-[12.5px] hover:border-primary/40"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-foreground">{d.fileName}</div>
                          <div className="text-[10.5px] text-muted-foreground">
                            {d.docKind.replace('_', ' ')} · {(d.sizeBytes / 1024).toFixed(0)} KB
                          </div>
                        </div>
                        <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      </a>
                    ))
                  )}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </>
        )}
      </DialogContent>

      {pr && (
        <ReceiptFormSheet open={receiptOpen} onOpenChange={setReceiptOpen} request={pr} />
      )}
    </Dialog>
  );
}

function ActionBar({
  pr,
  busy,
  onSubmit,
  onStartApprove,
  onStartReject,
  onConvert,
  onReceive,
}: {
  pr: PurchaseRequest;
  busy: boolean;
  onSubmit: () => void;
  onStartApprove: () => void;
  onStartReject: () => void;
  onConvert: () => void;
  onReceive: () => void;
}) {
  const fullyReceived = pr.items.every(
    (l) => Number(l.receivedQuantity) >= Number(l.quantity),
  );
  const canReceiveOpenMarket =
    pr.sourcingType === 'OPEN_MARKET' &&
    ['APPROVED', 'SOURCED', 'RECEIVED'].includes(pr.status) &&
    !fullyReceived;

  const buttons: React.ReactNode[] = [];
  if (pr.status === 'DRAFT') {
    buttons.push(
      <Button key="submit" size="sm" onClick={onSubmit} disabled={busy}>
        <ArrowRight className="h-3.5 w-3.5" /> Submit for approval
      </Button>,
    );
  }
  if (pr.status === 'PENDING_APPROVAL') {
    buttons.push(
      <Button key="approve" size="sm" onClick={onStartApprove} disabled={busy}>
        <Check className="h-3.5 w-3.5" /> Approve
      </Button>,
      <Button key="reject" size="sm" variant="outline" onClick={onStartReject} disabled={busy}>
        <Ban className="h-3.5 w-3.5" /> Reject
      </Button>,
    );
  }
  if (pr.status === 'APPROVED' && pr.sourcingType === 'VENDOR' && !pr.purchaseOrderId) {
    buttons.push(
      <Button key="convert" size="sm" onClick={onConvert} disabled={busy}>
        <Building2 className="h-3.5 w-3.5" /> Convert to PO
      </Button>,
    );
  }
  if (canReceiveOpenMarket) {
    buttons.push(
      <Button key="receive" size="sm" onClick={onReceive} disabled={busy}>
        <PackageCheck className="h-3.5 w-3.5" /> Record goods receipt
      </Button>,
    );
  }

  if (buttons.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-6 py-3">
      {buttons}
      {pr.sourcingType === 'VENDOR' && pr.purchaseOrder && (
        <>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <span className="text-[11.5px] text-muted-foreground">
            Receive against PO{' '}
            <span className="font-mono">{pr.purchaseOrder.poNumber}</span> in Purchase Orders.
          </span>
        </>
      )}
    </div>
  );
}
